'use client';

import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Select } from '@/components/atoms/Inputs/Select';
import { InputText } from '@/components/atoms/Inputs/InputText';
import profiler from '@/helpers/userProfile';
import { sqFeetToTonnageData, annualACHoursData } from './seerCalculatorData';
import { stateData } from './stateData';
import { StateAbbreviation, states } from '@/constants/states';
import { Button } from '@/components/atoms/Button';
import { useSearchParams } from 'next/navigation';
import { IconLeaf } from '@/components/icons';

export interface SEERSavingsCalculatorProps {
	testId?: string;
}

const seerSystems = [
	{ label: '6 SEER (common before 1990s)', value: '6' },
	{ label: '10 SEER (common in 1992-2005)', value: '10' },
	{ label: '13 SEER (common in 2006-2015)', value: '13' },
	{ label: '14 SEER (US North, 2015-present)', value: '14' },
	{ label: '15 SEER (US South, 2015-present)', value: '15' },
	{ label: '16 SEER (15.2 SEER2)', value: '16' },
	{ label: '17 SEER (16.2 SEER2)', value: '17' },
	{ label: '18 SEER (17.2 SEER2)', value: '18' },
	{ label: '19 SEER (18.1 SEER2)', value: '19' },
	{ label: '20 SEER (19.0 SEER2)', value: '20' },
	{ label: '21 SEER (20.0 SEER2)', value: '21' },
	{ label: '22 SEER (21.0 SEER2)', value: '22' },
	{ label: '23 SEER (22.0 SEER2)', value: '23' },
	{ label: '24 SEER (23.0 SEER2)', value: '24' },
	{ label: '25 SEER (24.0 SEER2)', value: '25' },
];

const SEERSavingsCalculator = (props: SEERSavingsCalculatorProps) => {
	const { testId = 'SEERSavingsCalculator' } = props;

	const params = useSearchParams();
	const stateParam = params.get('state');
	const costPerKwhParam = params.get('costPerKwh');
	const annualACHoursParam = params.get('annualACHours');
	const sqFeetTonnageParam = params.get('sqFeetTonnage');
	const currentSEERParam = params.get('currentSEER');
	const newSEERParam = params.get('newSEER');

	const getInitialValue = (param: string | null, options: string[]) =>
		param && options.includes(param) ? param : '';

	const [userProfile, updateProfile] = profiler();

	const [formData, setFormData] = useState({
		state: stateParam || userProfile.state,
		costPerKwh: costPerKwhParam || '',
		annualACHours: getInitialValue(
			annualACHoursParam,
			annualACHoursData.map((option) => option.value),
		),
		sqFeetTonnage: getInitialValue(
			sqFeetTonnageParam,
			sqFeetToTonnageData.map((option) => option.value),
		),
		currentSEER: getInitialValue(
			currentSEERParam,
			seerSystems.map((option) => option.value),
		),
		newSEER: getInitialValue(
			newSEERParam,
			seerSystems.map((option) => option.value),
		),
	});

	const {
		state,
		costPerKwh,
		annualACHours,
		sqFeetTonnage,
		currentSEER,
		newSEER,
	} = formData;

	const [stateErrorText, setStateErrorText] = useState('');
	const [sqFeetTonnageErrorText, setSqFeetTonnageErrorText] = useState('');
	const [costPerKwhErrorText, setCostPerKwhErrorText] = useState('');
	const [annualACHoursErrorText, setannualACHoursErrorText] = useState('');
	const [currentSEERErrorText, setCurrentSEERErrorText] = useState('');
	const [newSEERErrorText, setNewSEERErrorText] = useState('');
	const [updatedFromProfile, setUpdatedFromProfile] = useState(false);

	const handleStateChange = useCallback(
		(stateAbbreviation: StateAbbreviation) => {
			const data = stateData[stateAbbreviation];
			setFormData((prevFormData) => ({
				...prevFormData,
				costPerKwh: data.costPerKwh,
				annualACHours: data.coolingHours,
			}));
			setStateErrorText('');
			setCostPerKwhErrorText('');
			setannualACHoursErrorText('');

			updateProfile('state', stateAbbreviation);
		},
		[updateProfile],
	);

	// updates the state and costPerKwh and annualACHours when the userProfile state changes
	// this will only run once!
	useEffect(() => {
		if (
			!updatedFromProfile &&
			userProfile.state in stateData &&
			!costPerKwh &&
			!annualACHours
		) {
			handleStateChange(userProfile.state as StateAbbreviation);
			// sets updatedFromProfile to true to prevent further updates
			setUpdatedFromProfile(true);
		}
	}, [
		userProfile.state,
		costPerKwh,
		annualACHours,
		updatedFromProfile,
		handleStateChange,
	]);

	const handleChange = (
		e: ChangeEvent<HTMLSelectElement | HTMLInputElement>,
	) => {
		if (e.target.name === 'state' && e.target.value in stateData) {
			handleStateChange(e.target.value as StateAbbreviation);
		}

		// Clear errors
		if (e.target.name === 'costPerKwh') {
			setCostPerKwhErrorText('');
		}
		if (e.target.name === 'annualACHours') {
			setannualACHoursErrorText('');
		}
		if (e.target.name === 'sqFeetTonnage') {
			setSqFeetTonnageErrorText('');
		}
		if (e.target.name === 'currentSEER') {
			setCurrentSEERErrorText('');
		}
		if (e.target.name === 'newSEER') {
			setNewSEERErrorText('');
		}

		// Update State
		if (e.target.name === 'costPerKwh') {
			const costPerKwhValue = e.target.value.slice(1);
			const numberCheck = /[^\d.]+/;

			if (costPerKwhValue.match(numberCheck)) {
				return;
			}

			setFormData((prevFormData) => ({
				...prevFormData,
				costPerKwh: costPerKwhValue,
			}));
		} else {
			setFormData((prevFormData) => ({
				...prevFormData,
				[e.target.name]: e.target.value,
			}));
		}
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		const missingInfo =
			!state ||
			!costPerKwh ||
			!annualACHours ||
			!sqFeetTonnage ||
			!currentSEER ||
			!newSEER;

		const seerError = parseInt(newSEER) <= parseInt(currentSEER);

		setStateErrorText(!state ? 'Select state' : '');
		setCostPerKwhErrorText(!costPerKwh ? 'Enter electricity rate' : '');
		setannualACHoursErrorText(
			!annualACHours ? 'Select annual AC cooling usage' : '',
		);
		setSqFeetTonnageErrorText(!sqFeetTonnage ? 'Select square feet' : '');
		setCurrentSEERErrorText(!currentSEER ? 'Select current SEER System' : '');
		setNewSEERErrorText(!newSEER ? 'Select new SEER System' : '');
		if (seerError) {
			setNewSEERErrorText('New SEER must be greater than current SEER');
		}

		if (missingInfo || seerError) {
			e.preventDefault();
		}
	};

	return (
		<div
			className="rounded-2xl border-3 border-sky-600 bg-sky-50 px-4 pb-8 pt-6 lg:rounded-3xl
				lg:px-6 lg:pb-10 lg:pt-8"
			data-testid={testId}
		>
			<div className="mb-6 flex items-center justify-between lg:mb-8">
				<div className="text-xl font-medium text-sky-700 lg:text-2xl">
					SEER savings
					<span className="block text-sm text-sky-700 lg:text-base">
						Calculator
					</span>
				</div>
				<IconLeaf className="h-10 w-10 fill-sky-700 lg:h-12 lg:w-12" />
			</div>
			<form
				data-testid="SEERSavingsCalculatorForm"
				onSubmit={handleSubmit}
				action="/tools/seer-savings-calculator/results/"
			>
				<div className="flex flex-col gap-8 lg:flex-row">
					<div className="flex-1">
						<div className="mb-4">
							<Select
								id="state"
								label="State"
								name="state"
								onChange={handleChange}
								value={state}
								inputProps={{ className: '!bg-white' }}
								contextRowProps={{ 'data-testid': 'state' }}
								error={stateErrorText}
							>
								<option></option>
								{states.map((state) => (
									<option key={state.abbreviation} value={state.abbreviation}>
										{state.name}
									</option>
								))}
							</Select>
						</div>

						<div className="mb-4">
							<InputText
								id="costPerKwh"
								label={'Electricity rate (kWh)'}
								name="costPerKwh"
								value={'$' + costPerKwh}
								onChange={handleChange}
								error={costPerKwhErrorText}
							/>
						</div>

						<div className="mb-4">
							<Select
								id="annualACHours"
								label="Annual AC cooling usage"
								name="annualACHours"
								onChange={handleChange}
								value={annualACHours}
								inputProps={{ className: '!bg-white' }}
								contextRowProps={{ 'data-testid': 'annualACHours' }}
								error={annualACHoursErrorText}
							>
								<option></option>
								{annualACHoursData.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</Select>
						</div>
						<div className="mb-4">
							<Select
								id="sqFeetTonnage"
								label="Square feet"
								name="sqFeetTonnage"
								onChange={handleChange}
								value={sqFeetTonnage}
								inputProps={{ className: '!bg-white' }}
								contextRowProps={{ 'data-testid': 'sqFeetTonnage' }}
								error={sqFeetTonnageErrorText}
							>
								<option></option>
								{sqFeetToTonnageData.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</Select>
						</div>
					</div>
					{/* right column */}
					<div className="flex-1 gap-8">
						<div
							className="mb-8 rounded-2xl bg-white p-4 pb-6 shadow-xl shadow-sky-600/10 lg:rounded-3xl
								lg:p-6 lg:pb-8"
						>
							<div className="mb-1 text-xl font-semibold text-sky-700">
								Unit 1
							</div>
							<div className="mt-4">
								<Select
									id="currentSEER"
									label="SEER"
									name="currentSEER"
									onChange={handleChange}
									value={currentSEER}
									inputProps={{ className: '!bg-white' }}
									contextRowProps={{ 'data-testid': 'currentSEER' }}
									error={currentSEERErrorText}
								>
									<option></option>
									{seerSystems.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</Select>
							</div>
						</div>

						<div
							className="rounded-2xl bg-white p-4 pb-6 shadow-xl shadow-sky-600/10 lg:rounded-3xl lg:p-6
								lg:pb-8"
						>
							<div className="mb-1 text-xl font-semibold text-sky-700">
								Unit 2
							</div>
							<div className="mt-4">
								<Select
									id="newSEER"
									label="SEER"
									name="newSEER"
									onChange={handleChange}
									value={newSEER}
									inputProps={{ className: '!bg-white' }}
									contextRowProps={{ 'data-testid': 'newSEER' }}
									error={newSEERErrorText}
								>
									<option></option>
									{seerSystems.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</Select>
							</div>
						</div>
					</div>
				</div>
				<div className="mt-10 flex justify-center">
					<Button variant="large-default" className="w-full max-w-90">
						See my savings
					</Button>
				</div>
			</form>
		</div>
	);
};

export default SEERSavingsCalculator;
